import React, { useMemo } from "react";
import { MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useTableSettings } from "src/core/tables/useTableSettings";
import { CustomDataGrid } from "src/core/tables/CustomDataGrid";

/* ───────────────────── helpers ───────────────────── */
const flattenKeys = (obj: any, prefix = ""): string[] => {
  if (obj == null || typeof obj !== "object" || Array.isArray(obj)) return [];
  return Object.entries(obj).flatMap(([k, v]) => {
    const nk = prefix ? `${prefix}_${k}` : k;
    return typeof v === "object" && v !== null && !Array.isArray(v)
      ? flattenKeys(v, nk)
      : [nk];
  });
};

/** Retrieve a nested value using flattened key with "_" separator */
const getValueFromFlattenedKey = (obj: any, flatKey: string) => {
  return flatKey.split("_").reduce((acc, k) => (acc ? acc[k] : undefined), obj);
};

export const InspectionDialogButton: React.FC<Props> = ({ tableSettings, columns, }) => {
  /* 1 . Build inspection rows */
  const inspectionRows = useMemo(() => {
    const colFields = columns.map((c) => c.field);
    const rowFields = new Set<string>();
    (tableSettings?.allRows ?? []).forEach((r) => flattenKeys(r).forEach((k) => rowFields.add(k)),);
    const union = new Set([...colFields, ...rowFields]);
    let id = 0;
    return Array.from(union).map((fieldName) => {
      // first non-null / non-empty sample value
      let sample: any = null;
      for (const row of tableSettings?.allRows ?? []) {
        const val = getValueFromFlattenedKey(row, fieldName);
        if (val !== null && val !== undefined && val !== "") {
          sample = val;
          break;
        }
      }
      return {
        id: id++,
        field: fieldName,
        headerName: columns.find((c) => c.field === fieldName)?.headerName ?? "",
        inColumns: colFields.includes(fieldName),
        inRows: rowFields.has(fieldName),
        status: colFields.includes(fieldName) && rowFields.has(fieldName)
          ? "Both"
          : colFields.includes(fieldName)
            ? "Webclient only"
            : "Api Data only",
        sample,
      };
    });
  }, [columns, tableSettings?.allRows]);

  /* 2 . Column defs for the inner grid */
  const inspectionCols: GridColDef[] = [
    { field: "field", headerName: "API Name", width: 300 },
    { field: "headerName", headerName: "Webclient Table Header name", width: 300 },
    { field: "inColumns", headerName: "In Webclient Table", type: "boolean", width: 180 },
    { field: "inRows", headerName: "In API data", type: "boolean", width: 180 },
    { field: "status", headerName: "Status", flex: 1 },
    { field: "sample", headerName: "Sample value", flex: 1, minWidth: 200, renderCell: (params) => params.value === null ? (<span style={{ color: "red", fontWeight: "bold" }}>EMPTY</span>) : (<span>{String(params.value)}</span>), },
  ];

  /* 3 . Own tableSettings for the dialog’s grid */
  const inspectionTableSettings = useTableSettings({
    columns: inspectionCols,
    rows: inspectionRows,
    tableName: `${tableSettings?.tableName ?? "Unnamed"}_Inspection`,
    hasToolbar: false,
    hideFooter: true,
    checkboxSelection: false,
    idPath: ["id"],
  });
  inspectionTableSettings.loading = false;

  /* 4 . Open the generic TableDialog */
  const handleOpen = () => tableSettings.tableDialog.open({ title: "Data / column inspection", content: (<CustomDataGrid tableSettings={inspectionTableSettings} columns={inspectionCols} />), });

  return (
    <MenuItem onClick={handleOpen}>
      <ListItemIcon>
        <QueryStatsRoundedIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Inspect</ListItemText>
    </MenuItem>
  );
};

/* ───────────────────── component ──────────────────── */
interface Props {
  tableSettings: any;
  columns: GridColDef[];
}
